import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilm } from '@fortawesome/pro-light-svg-icons/faFilm';
import { faImage } from '@fortawesome/pro-light-svg-icons/faImage';
import { TypedMemo } from '../../../common/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { inlineMediaUrl } from '../inlineMediaUrl';
import { itemLabel, itemPath, itemPicture } from '../releases/list/allFiles/allFilesModel';
import { updatedAgo } from '../updatedAgo';
import { WorkEntry } from './workListModel';
import { workPageMessages } from './workPageMessages';
import { WorkRowFailedAction } from './WorkRowFailedAction';
import { WorkRowState } from './WorkRowState';
import { workRowText } from './workRowText';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Work.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WorkListRowProps {
  accountId: number;
  entry: WorkEntry;
  songTitle: string;
  /** The song of the row; undefined for a song that the catalog no longer holds. */
  catalogItem: CatalogItem | undefined;
  /** The demo artist: no "Try again". */
  readOnly: boolean;
  /** The file of the link that opened the page (`result=`, `reel=`). */
  isLinked: boolean;
  now: Date;
}

/**
 * One generation on the Work page (design assets-rethink e1 `.wk-r`): the picture, the name and the
 * song, the state (`WorkRowState`) and the actions. A failed row has the button of the old Work
 * row. Each row links to the home of the file: the Kit tab of the release for a keeper, Posts for
 * a post file, the archive for an archived post. The row of the link has a ring and a chip (f4).
 */
const WorkListRowComponent: React.FC<WorkListRowProps> = (props) => {
  const { accountId, entry, songTitle, catalogItem, readOnly, isLinked, now } = props;
  const intl = useIntl();
  const { item, row, state } = entry;
  const text = useMemo(() => (row === null ? null : workRowText(intl, row, catalogItem)), [catalogItem, intl, row]);
  const picture = itemPicture(item);
  const homeLabel =
    item.assetKind === 'keeper'
      ? workPageMessages.pageOpenKit
      : item.archivedAt !== null
        ? workPageMessages.pageOpenArchive
        : workPageMessages.pageOpenPosts;

  return (
    <li
      className={clsx(styles.row, state === 'failed' && styles.rowFailed, isLinked && styles.rowLinked)}
      data-testid="work-row"
      data-state={state}
      data-linked={isLinked ? 'true' : undefined}
    >
      {picture === null ? (
        // A file that is made has no picture yet: the box says if it is an image or a video.
        <span className={clsx(styles.thumb, styles.thumbNone)} aria-hidden="true">
          <FontAwesomeIcon icon={item.type === 'image' ? faImage : faFilm} />
        </span>
      ) : (
        <img className={styles.thumb} src={inlineMediaUrl(picture)} alt="" />
      )}
      <div className={styles.text}>
        <b className={styles.name}>{itemLabel(intl, item)}</b>
        <span className={styles.meta}>
          {text === null || text.meta === '' ? songTitle : `${songTitle} · ${text.meta}`}
        </span>
        {isLinked && (
          <span className={styles.linkChip} data-testid="work-from-link">
            {intl.formatMessage(workPageMessages.pageFromLink)}
          </span>
        )}
      </div>
      <WorkRowState
        state={state}
        row={row}
        text={text}
        ago={state === 'ready' ? updatedAgo(intl, new Date(entry.at).toISOString(), now) : null}
      />
      <div className={styles.actions}>
        {state === 'failed' && !readOnly && row !== null && catalogItem !== undefined && (
          <WorkRowFailedAction
            accountId={accountId}
            row={row}
            catalogItem={catalogItem}
            releaseTab={item.assetKind === 'keeper' ? 'kit' : 'posts'}
            className={styles.button}
            testID="work-retry"
          />
        )}
        <Link className={styles.link} to={itemPath(item)} data-testid="work-open">
          {intl.formatMessage(homeLabel)}
        </Link>
      </div>
    </li>
  );
};

export const WorkListRow = TypedMemo(WorkListRowComponent);
